import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    Component,
    computed,
    effect,
    inject,
} from "@angular/core";
import { rxResource, toObservable } from "@angular/core/rxjs-interop";
import { Router, RouterOutlet } from "@angular/router";
import { InfoService } from "@malirang/api-client";
import { ProductType } from "@malirang/common";
import { ModalService, Spinner } from "@malirang/front-share";
import { injectNavigationEnd } from "ngxtension/navigation-end";
import { Container } from "../../components/container/container.component";
import { IMenu, LayoutStore } from "../../stores/layout.store";
import { ProductTypeParams } from "../pages/store/product.page";
import { StoreMenuModal } from "../pages/store/store-menu/store-menu.modal";
import { menus } from "./const/menus";
import { SideMenuModule } from "./side-menu/side-menu.module";
import { TopBar } from "./top-bar/top-bar.component";

@Component({
    host: {
        ngSkipHydration: "true",
    },
    selector: "app-layout",
    templateUrl: "./layout.component.html",
    standalone: true,
    imports: [
        RouterOutlet,
        CommonModule,
        TopBar,
        SideMenuModule,
        Container,
        Spinner,
    ],
})
export class LayoutComponent {
    private readonly store = inject(LayoutStore);
    private readonly router = inject(Router);
    private readonly infoService = inject(InfoService);
    private readonly modalService = inject(ModalService);

    currentMenu = this.store.currentMenu;
    currentChildMenu = this.store.currentChildMenu;
    path = this.store.path;
    menus = this.store.menus;

    $storeMenu = rxResource({
        stream: () => this.infoService.infoControllerGetStoreMenuV1(),
    });
    storeMenu = computed(() => {
        return this.$storeMenu.hasValue() ? this.$storeMenu.value() : null;
    });
    storeMenu$ = toObservable(this.storeMenu);

    navigationEnd$ = injectNavigationEnd();

    constructor() {
        afterNextRender(() => {
            this.store.setMenus(menus);

            this.store.setPath(this.router.url);
            this.navigationEnd$.subscribe({
                next: () => {
                    this.store.setPath(this.router.url);
                },
            });
        });

        this.storeMenu$.subscribe({
            next: (storeMenu) => {
                if (storeMenu) {
                    const array = Object.keys(ProductType)
                        .map(
                            (key) => storeMenu[key as keyof typeof ProductType],
                        )
                        .sort((a, b) => a!.order - b!.order);
                    const order1 = array[0];
                    const order2 = array[1];
                    const order3 = array[2];

                    if (storeMenu) {
                        this.store.setMenus([
                            ...(menus || []),
                            {
                                name: "스토어 관리",
                                icon: "ic:baseline-storefront",
                                children: [
                                    {
                                        href: `/store/${ProductTypeParams[order1!.type]}`,
                                        name: order1!.name,
                                    },
                                    {
                                        href: `/store/${ProductTypeParams[order2!.type]}`,
                                        name: order2!.name,
                                    },
                                    {
                                        href: `/store/${ProductTypeParams[order3!.type]}`,
                                        name: order3!.name,
                                    },
                                    {
                                        href: "/store/menu",
                                        modal: StoreMenuModal,
                                        name: "스토어 메뉴 관리",
                                    },
                                    {
                                        href: "/store/form/digital",
                                        name: `${storeMenu.디지털자료?.name} 등록`,
                                        hidden: true,
                                    },
                                    {
                                        href: "/store/form/tool",
                                        name: `${storeMenu.교구?.name} 등록`,
                                        hidden: true,
                                    },
                                    {
                                        href: "/store/form/book",
                                        name: `${storeMenu.교재?.name} 등록`,
                                        hidden: true,
                                    },
                                    {
                                        href: "/store/detail",
                                        name: "상품 상세",
                                        hidden: true,
                                    },
                                ],
                            },
                        ]);
                    } else {
                        this.store.setMenus(menus);
                    }
                }
            },
        });
    }

    navigate(menu: IMenu) {
        if (menu.modal) {
            this.modalService.create(menu.modal).closed.subscribe({
                next: () => {
                    this.$storeMenu.reload();
                },
            });
            return;
        }

        this.router.navigateByUrl(menu.href || "");
    }
}
